import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Divider
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as AIIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  Lightbulb as IdeaIcon
} from '@mui/icons-material';
import { apiClient } from '../api/client';
import ForecastChart from '../components/ai/ForecastChart';
import ForecastSummary from '../components/ai/ForecastSummary';

interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  chartData?: any;  // Optional chart data
  agentType?: string;  // Type of agent that was executed
}

const AIChat: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: '👋 Hello! I\'m your AI Financial Assistant. I can help you with:\n\n• Calculations using figures you supply\n• General financial explanations\n\nI have not inspected your company records. Use company reports for analysis of verified records.\n\nWhat would you like to know?',
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiStatus, setAiStatus] = useState<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const [suggestions, setSuggestions] = useState<string[]>([]);

  useEffect(() => {
    // Check AI system status on mount
    checkAIStatus();
    setSuggestions([
      "For a hypothetical invoice totalling 500, 100 has been paid. What percentage is paid?",
      "Explain the difference between revenue and profit",
      "What figures are needed to calculate a profit margin?"
    ]);
  }, []);

  const checkAIStatus = async () => {
    try {
      const response = await apiClient.get('/ai/status');
      setAiStatus(response.data);
    } catch (err) {
      console.error('Failed to check AI status:', err);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage: Message = {
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/ai/chat', {
        message: input,
        conversation_history: messages.slice(1).map(m => ({
          role: m.role,
          content: m.content
        })),
        temperature: 0.7
      });

      // Check if an agent was executed and has chart-worthy data
      let chartData = null;
      let agentType = null;

      if (response.data.agent_executed && response.data.agent_result) {
        agentType = response.data.agent_type;
        const results = response.data.agent_result.results;

        // Check for forecast data
        if (agentType === 'forecast' && results?.forecast) {
          chartData = results.forecast;
        }
        // Could add more chart types here (expense analysis, etc.)
      }

      const assistantMessage: Message = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date(),
        chartData: chartData,
        agentType: agentType
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to get AI response';
      setError(errorMsg);

      const errorMessage: Message = {
        role: 'assistant',
        content: `❌ Error: ${errorMsg}`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([
      {
        role: 'assistant',
        content: '👋 Chat cleared! How can I help you?',
        timestamp: new Date()
      }
    ]);
    setError(null);
  };



  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ height: 'calc(100vh - 150px)', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AIIcon />
            <Typography variant="h6">AI Financial Assistant</Typography>
            {aiStatus && (
              <Chip
                label={aiStatus.ai_enabled ? 'Explanations configured' : 'Calculations available'}
                color={aiStatus.ai_enabled ? 'info' : 'default'}
                size="small"
                sx={{ ml: 2 }}
              />
            )}
          </Box>
          <IconButton color="inherit" onClick={clearChat} title="Clear chat">
            <RefreshIcon />
          </IconButton>
        </Box>

        {/* AI Status Info */}
        {aiStatus && aiStatus.ai_enabled && (
          <Box sx={{ p: 1, bgcolor: 'info.light', display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              Provider:
            </Typography>
            <Chip label={aiStatus.model || 'Unavailable'} size="small" />
            <Typography variant="caption" sx={{ ml: 2 }}>
              Calculations and read-only explanations
            </Typography>
          </Box>
        )}

        {!aiStatus?.ai_enabled && (
          <Alert severity="warning" sx={{ m: 2 }}>
            Model explanations are unavailable. You can still calculate supported percentages using supplied figures.
          </Alert>
        )}

        {/* Smart Suggestions */}
        <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="caption" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 'bold', color: 'primary.main' }}>
            <IdeaIcon fontSize="small" />
            Example questions (using synthetic figures):
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {suggestions.map((q, idx) => (
              <Chip
                key={idx}
                label={q}
                size="small"
                onClick={() => setInput(q)}
                sx={{
                  cursor: 'pointer',
                  bgcolor: 'primary.50',
                  color: 'primary.main',
                  fontWeight: 'medium',
                  '&:hover': { bgcolor: 'primary.100' }
                }}
              />
            ))}
          </Box>
        </Box>


        {/* Messages */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {messages.map((message, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                mb: 2
              }}
            >
              <Box
                sx={{
                  maxWidth: '70%',
                  display: 'flex',
                  gap: 1,
                  flexDirection: message.role === 'user' ? 'row-reverse' : 'row'
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: message.role === 'user' ? 'primary.main' : 'secondary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    flexShrink: 0
                  }}
                >
                  {message.role === 'user' ? <PersonIcon /> : <AIIcon />}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: '100%' }}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      bgcolor: message.role === 'user' ? 'primary.light' : 'grey.100',
                      color: message.role === 'user' ? 'white' : 'text.primary'
                    }}
                  >
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {message.content}
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.7 }}>
                      {message.timestamp.toLocaleTimeString()}
                    </Typography>
                  </Paper>

                  {/* Display chart if available */}
                  {message.chartData && message.agentType === 'forecast' && (
                    <Box sx={{ width: '600px', maxWidth: '100%' }}>
                      <ForecastChart data={message.chartData} />
                      <Box sx={{ mt: 2 }}>
                        <ForecastSummary data={message.chartData} />
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          ))}
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                AI is thinking...
              </Typography>
            </Box>
          )}
          <div ref={messagesEndRef} />
        </Box>

        <Divider />

        {/* Input */}
        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Ask for a calculation or financial explanation..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
              variant="outlined"
            />
            <Button
              variant="contained"
              color="primary"
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              sx={{ minWidth: 100 }}
              endIcon={<SendIcon />}
            >
              Send
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Calculations use only the figures you supply. Company records are not inspected by this chat.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default AIChat;
